/**
 * Récapitulatif pédagogique en langage clair pour l'étudiant.
 * Affiché en bas du relevé. Explique :
 *  - ce qu'il faut rattraper (si semestre non validé),
 *  - et le cas échéant qu'une UE / le semestre a été validé(e) par compensation.
 *
 * Règles institut 2I :
 *  - ECUE : ≥ 10 = validée ; < 10 = à rattraper.
 *  - UE   : ≥ 10 = validée ; 8–10 = compensable (validée si moy. semestre ≥ 10) ;
 *           6–8 = non validée ; < 6 = ÉLIMINATOIRE (matières à rattraper en priorité
 *           pour faire remonter la moyenne UE et déverrouiller le semestre).
 *  - Semestre : validé si moy. ≥ 10 ET aucune UE éliminatoire.
 */
import React from 'react';
import {
  CheckCircle2, AlertTriangle, BookOpen, Lightbulb, ShieldCheck, AlertOctagon,
} from 'lucide-react';
import { ReleveData, ReleveUE, ReleveMatiere } from '../services/portailApi';

interface Props {
  data: ReleveData;
}

interface MatiereAvecUE extends ReleveMatiere {
  ue_code: string;
  ue_nom: string;
}

function fmt(v: number | null | undefined, dec = 2): string {
  if (v === null || v === undefined) return '—';
  return Number(v).toFixed(dec).replace('.', ',');
}

export const RecapPedagogique: React.FC<Props> = ({ data }) => {
  const ues = data.ues || [];
  const res = data.resultat_semestre;
  const moySem = res?.moyenne_semestre ?? null;
  const semestreValide = res?.valide ?? false;

  // UE éliminatoires : moyenne UE < 6 → règle institut 2I.
  // Bloquent le semestre. Les matières de ces UE sont à rattraper EN PRIORITÉ
  // car améliorer leur note fait directement remonter la moyenne UE.
  const uesEliminatoires: ReleveUE[] = ues.filter(
    (ue) =>
      !ue.validee
      && ue.moyenne_ue !== null
      && ue.moyenne_ue !== undefined
      && ue.moyenne_ue < 6
  );

  // Matières prioritaires : toutes les matières des UE éliminatoires (peu importe
  // leur note ECUE individuelle) — rattraper celles avec la note la plus basse
  // d'abord pour maximiser le gain de moyenne UE.
  const matieresPrioritaires: MatiereAvecUE[] = uesEliminatoires.flatMap((ue) =>
    ue.matieres
      .filter((m) => m.moyenne_ecue !== null && m.moyenne_ecue !== undefined && m.moyenne_ecue < 10)
      .map((m) => ({ ...m, ue_code: ue.code_ue, ue_nom: ue.nom_ue }))
  ).sort((a, b) => (a.moyenne_ecue ?? 0) - (b.moyenne_ecue ?? 0));

  // UE non validées mais NON éliminatoires (6 ≤ moy < 10) — rattrapage classique
  const uesNonValideesNonElim: ReleveUE[] = ues.filter(
    (ue) =>
      !ue.validee
      && (ue.moyenne_ue === null || ue.moyenne_ue === undefined || ue.moyenne_ue >= 6)
  );

  // Matières secondaires à rattraper : dans les UE non-éliminatoires, celles < 10
  const matieresSecondaires: MatiereAvecUE[] = uesNonValideesNonElim.flatMap((ue) =>
    ue.matieres
      .filter((m) => m.moyenne_ecue !== null && m.moyenne_ecue !== undefined && m.moyenne_ecue < 10)
      .map((m) => ({ ...m, ue_code: ue.code_ue, ue_nom: ue.nom_ue }))
  ).sort((a, b) => (a.moyenne_ecue ?? 0) - (b.moyenne_ecue ?? 0));

  // UE validées par compensation : 8 ≤ moy_ue < 10 et UE marquée validée
  const uesCompensees: ReleveUE[] = ues.filter(
    (ue) =>
      ue.validee &&
      ue.moyenne_ue !== null &&
      ue.moyenne_ue !== undefined &&
      ue.moyenne_ue >= 8 &&
      ue.moyenne_ue < 10
  );

  // UE non validées (au sens large : éliminatoires + non validées non-éliminatoires)
  const uesNonValidees: ReleveUE[] = ues.filter((ue) => !ue.validee);
  // Total matières à rattraper (pour les compteurs)
  const totalMatieresRattrap = matieresPrioritaires.length + matieresSecondaires.length;

  // Aucune donnée → ne rien afficher
  if (!res) return null;

  // ---------- Cas A : semestre VALIDÉ ----------
  if (semestreValide) {
    const parCompensation = uesCompensees.length > 0;

    return (
      <div className="bg-green-900/20 border border-green-800/60 rounded-xl overflow-hidden">
        <div className="bg-green-900/40 px-4 py-3 border-b border-green-800/60 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-green-200">
              {parCompensation ? 'Semestre validé par compensation' : 'Semestre validé'}
            </div>
            <div className="text-xs text-green-300/80 mt-0.5">
              Moyenne : {fmt(moySem)}/20 — Tu n'as rien à rattraper.
            </div>
          </div>
        </div>

        <div className="p-4 space-y-3 text-sm text-gray-200">
          {parCompensation ? (
            <>
              <div className="flex items-start gap-2 text-gray-200">
                <Lightbulb size={16} className="text-yellow-400 flex-shrink-0 mt-0.5" />
                <p>
                  Tu as obtenu une moyenne générale d'au moins 10/20. Cela t'a permis de
                  valider <strong>par compensation</strong> les UE suivantes, dont la
                  moyenne se situe entre 8 et 10 :
                </p>
              </div>
              <ul className="space-y-1.5 ml-6">
                {uesCompensees.map((ue) => (
                  <li
                    key={ue.ue_id}
                    className="flex items-center gap-2 text-xs bg-gray-900/50 border border-green-800/40 rounded px-3 py-2"
                  >
                    <ShieldCheck size={14} className="text-green-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-100 truncate">{ue.nom_ue}</div>
                      <div className="text-[10px] text-gray-400 font-mono">{ue.code_ue}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-yellow-400 font-bold">{fmt(ue.moyenne_ue)}</div>
                      <div className="text-[10px] text-green-400">Compensée</div>
                    </div>
                  </li>
                ))}
              </ul>
              {ues.some(ue => ue.matieres.some(m => m.moyenne_ecue !== null && m.moyenne_ecue !== undefined && m.moyenne_ecue < 10)) && (
                <div className="bg-gray-900/40 border border-gray-700 rounded p-3 text-xs text-gray-300">
                  <div className="flex items-start gap-2">
                    <BookOpen size={14} className="text-blue-400 flex-shrink-0 mt-0.5" />
                    <p>
                      Certaines de tes matières ont une moyenne inférieure à 10, mais elles
                      sont <strong>compensées</strong> par tes bonnes notes dans les autres
                      matières de la même UE. <strong>Tu n'es pas obligé(e) de les
                      repasser.</strong>
                    </p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-start gap-2">
              <Lightbulb size={16} className="text-yellow-400 flex-shrink-0 mt-0.5" />
              <p>
                Bravo ! Toutes tes UE sont validées et tu as obtenu les crédits du
                semestre. Continue comme ça.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---------- Cas B : semestre NON validé ----------

  // Cause principale du non-passage
  let causePrincipale: string;
  if (uesEliminatoires.length > 0) {
    const noms = uesEliminatoires.map(u => u.code_ue || u.nom_ue).join(', ');
    causePrincipale = `Tu as ${uesEliminatoires.length} UE éliminatoire(s) (moyenne UE inférieure à 6/20) : ${noms}. Ces UE bloquent ton semestre — tu dois rattraper en priorité les matières qu'elles contiennent pour faire remonter leur moyenne.`;
  } else if (moySem !== null && moySem < 10) {
    causePrincipale = `Ta moyenne du semestre est de ${fmt(moySem)}/20. Pour valider, il faut au moins 10/20.`;
  } else if (uesNonValidees.length > 0) {
    causePrincipale = `Tu as ${uesNonValidees.length} UE non validée(s). Tu dois rattraper les matières en dessous de 10/20 dans ces UE.`;
  } else {
    causePrincipale = 'Le semestre n\'est pas encore validé. Voir ci-dessous les matières à repasser au rattrapage.';
  }

  return (
    <div className="bg-orange-900/20 border border-orange-800/60 rounded-xl overflow-hidden">
      <div className="bg-orange-900/40 px-4 py-3 border-b border-orange-800/60 flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-orange-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-orange-200">Semestre à rattraper</div>
          <div className="text-xs text-orange-300/80 mt-0.5">
            Moyenne : {fmt(moySem)}/20
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4 text-sm text-gray-200">
        {/* Cause principale */}
        <div className="bg-gray-900/50 border border-orange-800/40 rounded p-3 text-xs text-gray-200">
          <div className="flex items-start gap-2">
            <Lightbulb size={14} className="text-yellow-400 flex-shrink-0 mt-0.5" />
            <p>{causePrincipale}</p>
          </div>
        </div>

        {/* UE éliminatoires (synthèse) */}
        {uesEliminatoires.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2 text-red-300 font-semibold text-xs">
              <AlertOctagon size={14} />
              <span>UE éliminatoires (moyenne UE &lt; 6/20)</span>
            </div>
            <ul className="space-y-1.5">
              {uesEliminatoires.map((ue) => (
                <li
                  key={ue.ue_id}
                  className="flex items-center gap-2 text-xs bg-red-900/30 border border-red-700/60 rounded px-3 py-2"
                >
                  <AlertOctagon size={14} className="text-red-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-100 truncate">{ue.nom_ue}</div>
                    <div className="text-[10px] text-gray-400 font-mono mt-0.5">{ue.code_ue}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-red-300 font-bold text-base">{fmt(ue.moyenne_ue)}</div>
                    <div className="text-[10px] text-red-400">Éliminatoire</div>
                  </div>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-red-300/80 mt-2 italic flex items-start gap-1">
              <Lightbulb size={12} className="flex-shrink-0 mt-0.5" />
              <span>
                Tant qu'une UE a une moyenne strictement inférieure à 6, le semestre ne peut
                pas être validé même si la moyenne générale dépasse 10. <strong>Rattrape
                en priorité les matières ci-dessous</strong> pour faire remonter la moyenne UE
                au-dessus de 6 (et idéalement au-dessus de 10 pour valider l'UE).
              </span>
            </p>
          </div>
        )}

        {/* Matières prioritaires (dans les UE éliminatoires) */}
        {matieresPrioritaires.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2 text-red-200 font-semibold text-xs">
              <AlertOctagon size={14} />
              <span>🚨 Matières prioritaires ({matieresPrioritaires.length}) — UE éliminatoires</span>
            </div>
            <ul className="space-y-1.5">
              {matieresPrioritaires.map((m) => (
                <li
                  key={`${m.ue_code}-${m.matiere_id}`}
                  className="flex items-center gap-2 text-xs bg-red-900/20 border border-red-800/50 rounded px-3 py-2"
                >
                  <span className="text-red-400 text-sm font-bold w-5 text-center flex-shrink-0">!</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-100 truncate">{m.nom_matiere}</div>
                    <div className="text-[10px] text-gray-400 font-mono mt-0.5">
                      {m.code_matiere} • UE : {m.ue_code}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-red-400 font-bold">{fmt(m.moyenne_ecue)}</div>
                    <div className="text-[10px] text-gray-400">/20</div>
                  </div>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-red-300/80 mt-2 italic">
              Les matières les plus basses sont en haut — repassez-les pour améliorer
              la moyenne UE le plus efficacement possible.
            </p>
          </div>
        )}

        {/* Autres matières à rattraper (UE non éliminatoires mais non validées) */}
        {matieresSecondaires.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2 text-orange-200 font-semibold text-xs">
              <BookOpen size={14} />
              <span>Autres matières à rattraper ({matieresSecondaires.length})</span>
            </div>
            <ul className="space-y-1.5">
              {matieresSecondaires.map((m) => (
                <li
                  key={`${m.ue_code}-${m.matiere_id}`}
                  className="flex items-center gap-2 text-xs bg-gray-900/50 border border-orange-800/40 rounded px-3 py-2"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-100 truncate">{m.nom_matiere}</div>
                    <div className="text-[10px] text-gray-400 font-mono mt-0.5">
                      {m.code_matiere} • UE : {m.ue_code}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-orange-300 font-bold">{fmt(m.moyenne_ecue)}</div>
                    <div className="text-[10px] text-gray-400">/20</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Compteur global si rien à afficher */}
        {totalMatieresRattrap === 0 && uesEliminatoires.length === 0 && (
          <div className="bg-blue-900/20 border border-blue-800/40 rounded p-3 text-xs text-blue-200">
            Aucune matière individuellement à rattraper, mais ta moyenne semestre n'atteint
            pas 10/20. Tu peux essayer de repasser des matières pour améliorer ta moyenne.
          </div>
        )}

        {/* Conseil final */}
        <div className="bg-blue-900/20 border border-blue-800/40 rounded p-3 text-xs text-blue-200 flex items-start gap-2">
          <Lightbulb size={14} className="text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold mb-1">Comment ça se passe ?</p>
            <p>
              Tu repasses uniquement les matières listées ci-dessus lors de la session de
              rattrapage. La note la plus avantageuse entre la session normale et le
              rattrapage est conservée pour le calcul final.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
